import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { COMPANY } from "@/lib/config";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-slate flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute inset-0 hero-spotlight" />
      <div className="absolute inset-0 grid-overlay" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-brand-cyan mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <div className="glass-panel rounded-3xl p-8 cyan-glow">
          <div className="flex items-center gap-3 mb-6">
            <img src={COMPANY.logo} alt="Digitify My Biz" className="h-10 w-10 object-contain" />
            <div>
              <p className="font-heading font-extrabold text-white text-lg">Admin Portal</p>
              <p className="text-xs text-slate-400">Manage blog & leads</p>
            </div>
          </div>

          <form onSubmit={submit} data-testid="login-form" className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@digitifymybiz.com"
                className="mt-1.5 w-full rounded-xl bg-brand-slate/60 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-cyan transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 w-full rounded-xl bg-brand-slate/60 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-cyan transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue hover:bg-brand-blueHover px-6 py-3.5 font-semibold text-white transition-all duration-300 hover:scale-[1.01] active:scale-95 disabled:opacity-60"
            >
              <Lock className="h-4 w-4" /> {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
